import { parseUrlToIndexUiState } from '../algolia/url-handling';

/**
 * True when SearchContext `filtersKey()` output means "no structured filters".
 * Keep aligned with `filtersKey` in `contexts/SearchContext.tsx`.
 */
export function filtersKeyRepresentsEmptyFilters(key: string): boolean {
	if (key === 'u:') return true;
	if (key === 's:') return true;
	if (key === 'o:{}') return true;
	if (key.startsWith('o:')) {
		try {
			const parsed = JSON.parse(key.slice(2)) as unknown;
			if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
				return Object.keys(parsed as Record<string, unknown>).length === 0;
			}
		} catch {
			return false;
		}
	}
	return false;
}

function refinementListHasValues(rl: Record<string, string[]> | undefined): boolean {
	if (!rl || typeof rl !== 'object') return false;
	for (const v of Object.values(rl)) {
		if (!Array.isArray(v)) continue;
		if (v.some((x) => x != null && String(x).trim() !== '')) return true;
	}
	return false;
}

/** True when `href` (path + query + path-encoded facets) still carries search refinements. */
export function urlHrefHasSearchRefinements(href: string): boolean {
	try {
		const ui = parseUrlToIndexUiState(href);
		return refinementListHasValues(ui.refinementList);
	} catch {
		return false;
	}
}

/**
 * Layout `searchData.initialFilters` can briefly become `{}` during CSR/Vike updates while
 * `window.location` still reflects the user's filters. Applying that empty prop would clear
 * `filtersRef` and snap results back to an unfiltered cache hit.
 *
 * When this returns true, SearchProvider should **not** overwrite filters from `initialFilters`.
 */
export function shouldIgnoreEmptyInitialFiltersFromLayout(opts: {
	syncToUrl: boolean;
	nextFiltersKey: string;
	windowHref: string;
}): boolean {
	if (!opts.syncToUrl) return false;
	if (!filtersKeyRepresentsEmptyFilters(opts.nextFiltersKey)) return false;
	return urlHrefHasSearchRefinements(opts.windowHref);
}
